import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  Checkbox as CheckboxMUI,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Icon,
  Chip,
} from "@mui/material";
import { IconChevronsLeft, IconEdit } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import {
  CaseInfoFileStatus,
  RefferalTypes,
  RepType,
} from "../../../constants/MenuSelection";
import UploadIcon from "@mui/icons-material/Upload";
import useAuth from "../../../hooks/useAuth";
import useCases from "../../../hooks/useCases";
import usePayment from "../../../hooks/usePayment";
import useUser from "../../../hooks/useUser";
import { capitalizeFirstLetter } from "../../../utils/capitalizeFirstLetter";
import { sanitizePath } from "../../../utils/sanitizePath";
import { useSnackbarHelper } from "../../components/Snackbar";
import BasicButton from "../../components/buttons/BasicButton";
import MainCard from "../../components/cards/MainCard";
import BasicAutocomplete from "../../components/inputs/BasicAutocomplete";
import BasicDatePicker from "../../components/inputs/BasicDatePicker";
import Checkbox from "../../components/inputs/CheckBox";
import MuiTextField from "../../components/inputs/MuiTextField";
import NumberField from "../../components/inputs/NumberField";
import { uploadImageToS3 } from "../../components/common/uploadImages";
import { stripS3Prefix } from "../../../utils/s3";
import TableSkeleton from "../../components/skeleton/TableSkeleton";
import SelectWorkflowModal from "./SelectWorkflowModal";
import { useModal } from "mui-modal-provider";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import useTodo from "../../../hooks/useTodo";

export type ClientFormValues = any;

const CaseInfo = ({ caseData, hideRoster, isCaseClosed, isStaff }: any) => {
  const { lawyers, staff } = useUser();
  const { showModal } = useModal();
  const [isTriggerWorkflow, setIsTriggerWorkflow] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);

  const [file, setFile] = useState<any>();
  const fileInputRef = useRef<HTMLInputElement | null>(null); // Ref for the hidden file input
  const { updateCase, isCaseUpdating, boardMembers } = useCases(caseData?._id);
  const { refetchProgressHistory, isLoadingProgressHistory } = useTodo(
    caseData?._id,
    undefined,
    undefined,
    undefined,
    true,
  );

  const showSnackbar = useSnackbarHelper();
  const [isLoading, setIsLoading] = useState(false);
  const innerWidth = window?.innerWidth;

  const { paymentScheduling } = usePayment(caseData?._id);

  const isPaymentCompleted = useMemo(() => {
    return paymentScheduling?.payment?.every(
      (payment: any) => payment.isPaymentCompleted === true,
    );
  }, [paymentScheduling]);

  const initialValues = useMemo(
    () => ({
      caseStatus: caseData?.caseStatus,
      mailType: caseData?.mailType || "",
      // phoneType: caseData?.phoneType || "",
      repType: caseData?.repType || "",
      feeQuoted: caseData?.feeQuoted || "",
      isAllLawyer: caseData?.isAllLawyer || false,
      isAllStaff: caseData?.isAllStaff || false,
      lawyer: caseData?.lawyer?._id || null,
      secondaryLawyer: caseData?.secondaryLawyer || null,
      staff: caseData?.staff || null,
      initialContactDate: caseData?.initialContactDate || null,
      followUpDate: caseData?.followUpDate?.[0] || null,
      progress: caseData?.progress || "",
      retentionDate: caseData?.retentionDate || null,
      leadVoter: caseData?.leadVoter || "",
      isExtraordinaryVoteCase: caseData?.isExtraordinaryVoteCase || false,
      isExistingClient: caseData?.isExistingClient || false,
      // isUnknown: caseData?.isUnknown || false,
      paroleHiringDateTime: caseData?.paroleHiringDateTime || null,
      referralType: caseData?.referralType || "",
      referralDetails: caseData?.referralDetails || "",
      isClientHiredAnotherAttorney:
        caseData?.isClientHiredAnotherAttorney || false,
      hiredAnotherAttorneyReason: caseData?.hiredAnotherAttorneyReason || "",
      // isHouseBill: caseData?.isHouseBill || false,
    }),
    [caseData],
  );

  const members = boardMembers?.map((option: any) => {
    return { label: option?.name, value: option?.name };
  });

  const validationSchema: any = Yup.object({
    // caseId: Yup.string().required(),
    caseStatus: Yup.string().required("Required"),
    repType: Yup.string().required("Required"),
    mailType: Yup.string().required("Required"),
    // phoneType: Yup.string().required("Required"),
    feeQuoted: Yup.string(),
    // lawyer: Yup.string().when("caseStatus", (caseStatus: any, schema: any) => {
    //   return caseStatus?.[0] !== "active"
    //     ? schema.nullable()
    //     : schema.required("Required");
    // }),
    // lawyer: Yup.string().required("Required"),
    // staff: Yup.string().required("Required"),
    isAllLawyer: Yup.boolean(),
    isAllStaff: Yup.boolean(),

    lawyer: Yup.string().when(
      "isAllLawyer",
      (isAllLawyer: any, schema: any) => {
        return isAllLawyer?.[0]
          ? schema.nullable()
          : schema.required("Required");
      },
    ),
    staff: Yup.string().when("isAllStaff", (isAllStaff: any, schema: any) => {
      return isAllStaff?.[0] ? schema.nullable() : schema.required("Required");
    }),
    secondaryLawyer: Yup.string().nullable(),
    initialContactDate: Yup.date().nullable(),
    followUpDate: Yup.date().nullable(),
    progress: Yup.string().nullable(),
    retentionDate: Yup.date().nullable(),
    leadVoter: Yup.string().notRequired(),
    isExtraordinaryVoteCase: Yup.boolean(),
    // isHouseBill: Yup.boolean(),
    // isUnknown: Yup.boolean(),
    paroleHiringDateTime: Yup.date().nullable(),
    referralType: Yup.string().nullable(),
    referralDetails: Yup.string(),
    isClientHiredAnotherAttorney: Yup.boolean(),
    hiredAnotherAttorneyReason: Yup.string().nullable(),
  });

  const { request, userId } = useAuth();
  const { repTypes } = useUser(userId);
  const formContext = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const { watch, setValue } = formContext;
  const caseStatus = watch("caseStatus");
  const repType = watch("repType");
  // const lawyer = watch("lawyer");
  const isAllLawyer = watch("isAllLawyer");
  const isAllStaff = watch("isAllStaff");
  const secondaryLawyer = watch("secondaryLawyer");

  const isClientHiredAnotherAttorney = caseData?.isClientHiredAnotherAttorney;
  const isAnotherAttorney = watch("isClientHiredAnotherAttorney");

  useEffect(() => {
    if (!isTriggerWorkflow) {
      setSelectedTaskIds([]);
    }
  }, [isTriggerWorkflow]);

  useEffect(() => {
    if (caseData?.representingInfo?.continueRepresentingThisCase === "yes") {
      return;
    }

    //set feeQuoted according to fee type
    const matchingRepType = repTypes?.find(
      (rep: any) => rep?.type.toLowerCase() === repType?.toLowerCase(),
    );

    if (matchingRepType && repType !== caseData?.repType) {
      setValue("feeQuoted", matchingRepType.value);
    } else {
      setValue("feeQuoted", caseData?.feeQuoted);
    }
  }, [repType, setValue, repTypes, caseData]);

  const mailTypes = useMemo(() => {
    if (!caseData?.contactId?.email) return [];
    const array = caseData?.contactId?.email.map((item: any) => {
      return {
        label: capitalizeFirstLetter(item?.emailType),
        value: item?.emailType,
      };
    });
    return array;
  }, [caseData]);

  // const phoneTypes = useMemo(() => {
  //   if (!caseData?.contactId?.phone) return [];
  //   const array = caseData?.contactId?.phone.map((item: any) => {
  //     return {
  //       label: capitalizeFirstLetter(item?.phoneType),
  //       value: item?.phoneType,
  //     };
  //   });
  //   return array;
  // }, [caseData]);
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // Enforce maximum file size of 5MB
      if (selectedFile.size > 5 * 1024 * 1024) {
        // clear the input so the user can re-select
        event.target.value = "";
        showSnackbar("File size should be less than 5MB", "error");
        return;
      }

      setFile(selectedFile);
    }
  };

  function shortenFileName(fileName: string, maxLength: number = 20): string {
    const extensionIndex = fileName.lastIndexOf(".");

    // Handle files without extensions
    if (extensionIndex === -1 || fileName.length <= maxLength) {
      return fileName; // Return original if it's shorter than maxLength
    }

    const name = fileName.substring(0, extensionIndex); // File name without extension
    const extension = fileName.substring(extensionIndex); // File extension

    if (fileName.length <= maxLength) {
      return fileName;
    }

    // Start part (first N characters) and end part (last N characters of name)
    const startPart = name.substring(0, 15);
    const endPart = name.substring(name.length - 5);

    return `${startPart}..${endPart}${extension}`;
  }

  const openSelectWorkflowModal = useCallback(
    (shouldOpen: boolean, tasks: any) => {
      if (shouldOpen) {
        const modal: any = showModal(SelectWorkflowModal, {
          onClose: () => {
            modal.hide();
          },
          onAdd: (selectedTasks: any[]) => {
            // console.log("Received in parent:", selectedTasks); // ✅ handle in parent
            setSelectedTaskIds(selectedTasks);
            modal.hide();
          },
          data: shouldOpen,
          previousSelectedTasks: tasks,
        });
      }
    },
    //eslint-disable-next-line
    [],
  );

  const onFormSubmit = async (values: any) => {
    try {
      setIsLoading(true);
      let docuSignUrl = caseData?.docuSignUrl || null;
      if (caseData?.isExistingClient && caseData?.docuSignUrl === null) {
        const folderName = "document";
        const fileData = {
          file: file,
          userId: userId,
          folderName: folderName,
          request,
        };
        let res: any;

        try {
          if (file) {
            res = await uploadImageToS3(fileData);
            if (res?.Location) {
              docuSignUrl = stripS3Prefix(res.Location); // Assign the uploaded file URL
            }
          }
        } catch (error: any) {
          showSnackbar(error?.message || "Upload failed", "error");
        }
        //New
        const caseId = caseData?.caseId;
        const inmateName = caseData?.contactId?.inmate?.name
          ? caseData?.contactId?.inmate?.name
          : "County_Inmate";
        const UploadDocumentFolderName = "Uploaded Documents";
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const fileName =
          sanitizePath(file?.name) || `Rep Agreement${timestamp}`;
        const uploadedDocumentFolderName = `Cases/${sanitizePath(inmateName)}-${sanitizePath(caseId)}/${UploadDocumentFolderName}/${fileName}`;
        // "document";
        const uploadedDocumentFileData = {
          file: file,
          userId: userId,
          folderName: uploadedDocumentFolderName,
          request,
        };

        try {
          if (file) {
            await uploadImageToS3(uploadedDocumentFileData);
          }
        } catch (error: any) {
          showSnackbar(error?.message || "Upload failed", "error");
        }
      }

      const selectedTasks = selectedTaskIds?.map((t: any) => t?._id);

      const docuSignStatus =
        caseData?.isExistingClient && docuSignUrl !== null
          ? "COMPLETED"
          : caseData?.docuSignStatus;
      const data: any = await updateCase({
        ...values,
        docuSignStatus,
        docuSignUrl,
        triggerWorkflow: isTriggerWorkflow,
        existingClientCompletedTasks: selectedTasks,
      });

      const message = data?.message || "Success";
      if (data) {
        setIsLoading(false);
      }
      showSnackbar(message, "success");
      refetchProgressHistory();
    } catch (error: any) {
      setIsLoading(false);
      const message = error?.response?.data?.message || "An error occurred";

      showSnackbar(message, "error");
    }
  };

  return (
    <MainCard
      allowFormContainer={true}
      formContext={formContext}
      onSubmit={onFormSubmit}
      title={
        <Typography variant={innerWidth < 1420 ? "h2" : "h1"}>
          Case Info
        </Typography>
      }
      subheader={
        <Typography variant="h6">{`#${caseData?.caseId || "000001"}`}</Typography>
      }
      content={
        <Box
          sx={{
            height: "calc(100vh - 220px)",
            overflowY: "auto",
            p: 1,
            overflowX: "hidden",
          }}
        >
          {isLoading ? (
            <TableSkeleton />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <BasicAutocomplete
                  // disabled={isStaff}
                  label="Case Status"
                  name="caseStatus"
                  required
                  options={CaseInfoFileStatus}
                  getOptionValue={(option: any) => option?.value}
                />
              </Grid>
              <Grid item xs={12}>
                <BasicAutocomplete
                  label="Rep Type"
                  name="repType"
                  required
                  options={RepType}
                  // disabled={!isPaymentCompleted || isCaseClosed || isStaff}
                  disabled={!isPaymentCompleted || isCaseClosed}
                  getOptionValue={(option: any) => option?.value}
                />
              </Grid>
              <Grid item xs={12}>
                <NumberField
                  label="Fee Quoted"
                  name="feeQuoted"
                  prefix="$ "
                  decimalScale={2}
                  fixedDecimalScale={true}
                  max={999999}
                  disabled={
                    caseData?.representingInfo?.continueRepresentingThisCase ===
                      "yes" ||
                    !isPaymentCompleted ||
                    isCaseClosed
                    // isStaff
                  }
                  // options={Task}
                />
              </Grid>
              <Grid item xs={12}>
                <BasicAutocomplete
                  label="Mail Type"
                  name="mailType"
                  options={mailTypes}
                  required
                  getOptionValue={(option: any) => option?.value}
                  // disabled={isCaseClosed || isStaff}
                  disabled={isCaseClosed}
                />
              </Grid>
              {/* <Grid item xs={12}>
              <BasicAutocomplete
                label="Phone Type"
                name="phoneType"
                options={phoneTypes}
                required
                getOptionValue={(option: any) => option?.value}
                disabled={isCaseClosed || isStaff}
              />
            </Grid> */}
              <Grid item sm={6}>
                <Checkbox
                  name="isAllLawyer"
                  label="All Lawyers"
                  disabled={isCaseClosed}
                />
              </Grid>
              <Grid item sm={6}>
                <Checkbox
                  name="isAllStaff"
                  label="All Staff"
                  disabled={isCaseClosed}
                />
              </Grid>
              {!isAllLawyer && (
                <Grid item xs={12}>
                  <BasicAutocomplete
                    label="Lawyer"
                    name="lawyer"
                    options={lawyers
                      ?.filter((user: any) => user?._id !== secondaryLawyer)
                      ?.map((user: any) => ({
                        label: `${user.firstName} ${user.lastName}`,
                        value: user?._id,
                      }))}
                    getOptionValue={(option: any) => option?.value}
                    required={
                      caseStatus === "active" ? true : false || !isAllLawyer
                    }
                    // disabled={isCaseClosed || isStaff}
                    disabled={isCaseClosed || isAllLawyer}
                  />
                </Grid>
              )}
              {/* <Grid item xs={12}>
              <BasicAutocomplete
                label="Secondary Lawyer"
                name="secondaryLawyer"
                options={lawyers
                  ?.filter((user: any) => user?._id !== lawyer)
                  ?.map((user: any) => ({
                    label: `${user.firstName} ${user.lastName}`,
                    value: user?._id,
                  }))}
                getOptionValue={(option: any) => option?.value}
                // disabled={isCaseClosed || isStaff}
                disabled={isCaseClosed}
              />
            </Grid> */}
              {!isAllStaff && (
                <Grid item xs={12}>
                  <BasicAutocomplete
                    label="Staff"
                    name="staff"
                    required
                    options={staff?.map((user: any) => ({
                      label: `${user.firstName} ${user.lastName}`,
                      value: user?._id,
                    }))}
                    getOptionValue={(option: any) => option?.value}
                    // disabled={isCaseClosed || isStaff}
                    disabled={isCaseClosed || isAllStaff}
                  />
                </Grid>
              )}

              {caseData?.isExistingClient && caseData?.docuSignUrl === null && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack spacing={2}>
                      {/* Align button at the end */}
                      <Typography variant="h5">Rep Agreement:</Typography>
                      <Button
                        variant="contained"
                        component="span"
                        sx={{ textTransform: "none", mb: 1 }}
                        onClick={() => {
                          fileInputRef.current?.click();
                        }} // Trigger the file input
                        startIcon={<UploadIcon />}
                      >
                        Upload
                      </Button>
                      <input
                        type="file"
                        ref={fileInputRef} // Hidden file input
                        accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        style={{ display: "none" }} // Hide the file input element
                        onChange={handleFileSelect}
                      />
                      {file && (
                        <Typography>{shortenFileName(file?.name)}</Typography>
                      )}
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                  </Grid>
                </>
              )}
              {!caseData?.contactId?.isCountyInmate &&
                caseData?.isExistingClient &&
                !caseData?.workflowId && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 0.5 }} />
                    </Grid>
                    <Grid item xs={12}>
                      {/* <Checkbox
                      name="triggerWorkflow"
                      label="Trigger workflows now?"
                      disabled={!file}
                      // onChange={(e: any) => console.log(e.target.value)}
                      // labelPlacement="end"
                    /> */}

                      <FormControlLabel
                        control={
                          <CheckboxMUI
                            name="triggerWorkflow"
                            value={isTriggerWorkflow}
                            disabled={!file && !caseData?.docuSignUrl}
                            onChange={(e: any) => {
                              openSelectWorkflowModal(
                                e.target.checked,
                                selectedTaskIds,
                              );
                              setIsTriggerWorkflow(e.target.checked);
                            }}
                            inputProps={{
                              "aria-label": "Trigger workflows now?",
                            }}
                          />
                        }
                        labelPlacement="start"
                        label="Trigger workflows now?"
                      />
                      <Tooltip
                        title={
                          "Do you want to trigger the workflow now, or wait until the first scheduled payment is made? If you want to trigger it now, please check the box.\nNote: You can only enable this checkbox after uploading a Representation Agreement."
                        }
                        PopperProps={{
                          style: { whiteSpace: "pre-line" },
                        }}
                      >
                        <Icon sx={{ ml: 3, mb: -1 }}>
                          <InfoOutlinedIcon
                            sx={{ fontSize: 20, color: "#008EBB" }}
                          />
                        </Icon>
                      </Tooltip>
                    </Grid>

                    {isTriggerWorkflow && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 0.5 }} />
                        </Grid>
                        <Grid item xs={8} mt={-1} mb={-1}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            Task(s) Already Completed
                          </Typography>
                        </Grid>

                        <Grid
                          item
                          xs={4}
                          mt={-2}
                          mb={-1}
                          display={"flex"}
                          justifyContent={"flex-end"}
                        >
                          <Tooltip
                            title="Edit"
                            // placement="top"
                          >
                            <IconButton
                              onClick={() =>
                                openSelectWorkflowModal(true, selectedTaskIds)
                              }
                            >
                              <IconEdit style={{ cursor: "pointer" }} />
                            </IconButton>
                          </Tooltip>
                        </Grid>

                        <Grid item xs={12}>
                          {selectedTaskIds?.length > 0 ? (
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                              {selectedTaskIds.map((t: any) => (
                                <Chip
                                  key={t._id}
                                  label={t.task}
                                  variant="outlined"
                                  color="primary"
                                  onDelete={() =>
                                    setSelectedTaskIds((prev: any) =>
                                      prev.filter(
                                        (task: any) => task._id !== t._id,
                                      ),
                                    )
                                  }
                                />
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No tasks selected.
                            </Typography>
                          )}
                        </Grid>

                        <Grid item xs={12}>
                          <Divider sx={{ my: 0.5 }} />
                        </Grid>
                      </>
                    )}
                  </>
                )}
              <Grid item xs={12}>
                <BasicDatePicker
                  label="Initial Contact Date"
                  name="initialContactDate"
                  // disabled={isCaseClosed || isStaff}
                  disabled={isCaseClosed}
                />
              </Grid>
              <Grid item xs={12}>
                <BasicDatePicker
                  label="Follow Up Date"
                  name="followUpDate"
                  // disabled={isCaseClosed || isStaff}
                  disabled={isCaseClosed}
                />
              </Grid>
              <Grid item xs={12}>
                <BasicDatePicker
                  label="Hiring Date"
                  name="paroleHiringDateTime"
                  // disabled={isCaseClosed || isStaff}
                  disabled={isCaseClosed}
                />
              </Grid>

              <Grid item xs={12}>
                <BasicDatePicker
                  label="Retention Date"
                  name="retentionDate"
                  // disabled={isCaseClosed || isStaff}
                  disabled={isCaseClosed}
                />
              </Grid>
              <Grid item xs={12}>
                <BasicAutocomplete
                  label="Lead Voter"
                  name="leadVoter"
                  freeSolo
                  options={members}
                  required={false}
                  getOptionValue={(option: any) => option?.value}
                  // disabled={isCaseClosed || isStaff}
                  disabled={isCaseClosed}
                />
              </Grid>
              <Grid item xs={6}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Checkbox
                    name="isExtraordinaryVoteCase"
                    label="Extraordinary Vote Case?"
                    // disabled={isCaseClosed || isStaff}
                    disabled={isCaseClosed}
                  />
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Checkbox
                    name="isExistingClient"
                    label="Is Existing Client?"
                    disabled
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <BasicAutocomplete
                  label="Referral Type"
                  name="referralType"
                  options={RefferalTypes}
                  getOptionValue={(option: any) => option?.value}
                  // disabled={isCaseClosed || isStaff}
                  disabled={isCaseClosed}
                />
              </Grid>
              <Grid item xs={12}>
                <MuiTextField
                  minRows={2}
                  multiline
                  label="Referral Details"
                  // placeholder="Referral Details"
                  name="referralDetails"
                  sx={{ borderColor: "#B2BAC2" }}
                  // options={Task}
                  //
                  // disabled={isCaseClosed || isStaff}
                  disabled={isCaseClosed}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Checkbox
                    name="isClientHiredAnotherAttorney"
                    label="Has the client hired another attorney?"
                    // disabled={
                    //   isCaseClosed || isStaff || isClientHiredAnotherAttorney
                    // }
                    disabled={isCaseClosed || isClientHiredAnotherAttorney}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <MuiTextField
                  minRows={2}
                  multiline
                  label="Reason"
                  // placeholder="Referral Details"
                  name="hiredAnotherAttorneyReason"
                  sx={{ borderColor: "#B2BAC2" }}
                  // options={Task}
                  //
                  // disabled={isCaseClosed || isStaff || !isAnotherAttorney}
                  disabled={isCaseClosed || !isAnotherAttorney}
                />
              </Grid>
            </Grid>
          )}
        </Box>
      }
      actions={
        <Stack direction={"row"} alignItems={"center"}>
          {/* {!isStaff && ( */}
          <BasicButton
            label={"Save"}
            type="submit"
            variant="contained"
            color="success"
            disabled={isCaseUpdating}
          />
          {/* )} */}
          <Tooltip title="Hide Case Info">
            <IconButton color="primary" onClick={hideRoster} id="modalShow">
              <IconChevronsLeft />
            </IconButton>
          </Tooltip>
        </Stack>
      }
    />
  );
};

export default CaseInfo;
